import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low';
export type FindingStatus = 'open' | 'in_progress' | 'resolved';
export type ReviewStatus = 'not_reviewed' | 'reviewed' | 'flagged' | 'pending_evidence';
export type CycleStatus = 'planned' | 'active' | 'completed';
export type AuditItemType = 'policy' | 'task' | 'control' | 'evidence';

export interface AuditCycle {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  status: CycleStatus;
  scheduled_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AuditFinding {
  id: string;
  company_id: string;
  related_item_id: string | null;
  related_item_type: AuditItemType | null;
  program_id: string | null;
  control_id: string | null;
  evidence_item_id: string | null;
  severity: FindingSeverity;
  description: string;
  raised_by: string;
  assigned_to: string | null;
  immediate_action: string | null;
  corrective_action: string | null;
  audit_cycle_id: string | null;
  status: FindingStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  raiser?: { id: string; name: string };
  assignee?: { id: string; name: string } | null;
}

export interface AuditReviewItem {
  id: string;
  company_id: string;
  item_id: string;
  item_type: AuditItemType;
  program_id: string | null;
  control_id: string | null;
  evidence_item_id: string | null;
  review_status: ReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

export interface CompanyProfile {
  id: string;
  name: string;
  email: string;
}

export function useAuditFindings() {
  const { profile } = useAuth();
  const [findings, setFindings] = useState<AuditFinding[]>([]);
  const [reviewItems, setReviewItems] = useState<AuditReviewItem[]>([]);
  const [cycles, setCycles] = useState<AuditCycle[]>([]);
  const [profiles, setProfiles] = useState<CompanyProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!profile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      const [findingsRes, reviewRes, cyclesRes, profilesRes] = await Promise.all([
        supabase
          .from('audit_findings')
          .select(`
            *,
            raiser:profiles!audit_findings_raised_by_fkey(id, name),
            assignee:profiles!audit_findings_assigned_to_fkey(id, name)
          `)
          .eq('company_id', profile.company_id)
          .order('created_at', { ascending: false }),
        supabase
          .from('audit_review_items')
          .select('*')
          .eq('company_id', profile.company_id),
        supabase
          .from('audit_cycles')
          .select('*')
          .eq('company_id', profile.company_id)
          .order('created_at', { ascending: false }),
        supabase
          .from('profiles')
          .select('id, name, email')
          .eq('company_id', profile.company_id)
          .order('name'),
      ]);

      if (findingsRes.error) throw findingsRes.error;
      if (reviewRes.error) throw reviewRes.error;
      if (cyclesRes.error) throw cyclesRes.error;
      if (profilesRes.error) throw profilesRes.error;

      setFindings((findingsRes.data || []) as AuditFinding[]);
      setReviewItems((reviewRes.data || []) as AuditReviewItem[]);
      setCycles((cyclesRes.data || []) as AuditCycle[]);
      setProfiles((profilesRes.data || []) as CompanyProfile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id]);

  useEffect(() => {
    if (!profile?.company_id) return;
    fetchAll();

    const channel = supabase
      .channel('audit-findings-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'audit_findings', filter: `company_id=eq.${profile.company_id}` }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'audit_review_items', filter: `company_id=eq.${profile.company_id}` }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'audit_cycles', filter: `company_id=eq.${profile.company_id}` }, fetchAll)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [profile?.company_id]);

  const raiseFinding = async (params: {
    relatedItemId: string | null;
    relatedItemType: AuditItemType | null;
    programId?: string | null;
    controlId?: string | null;
    evidenceItemId?: string | null;
    severity: FindingSeverity;
    description: string;
    assignedTo?: string | null;
    auditCycleId?: string | null;
  }) => {
    if (!profile?.company_id || !profile?.id) throw new Error('Not authenticated');

    const { error: insertError } = await supabase.from('audit_findings').insert({
      company_id: profile.company_id,
      related_item_id: params.relatedItemId,
      related_item_type: params.relatedItemType,
      program_id: params.programId || null,
      control_id: params.controlId || null,
      evidence_item_id: params.evidenceItemId || null,
      severity: params.severity,
      description: params.description,
      raised_by: profile.id,
      assigned_to: params.assignedTo || null,
      audit_cycle_id: params.auditCycleId || null,
      status: 'open',
    });

    if (insertError) throw insertError;

    if (params.relatedItemId && params.relatedItemType) {
      await upsertReviewStatus(params.relatedItemId, params.relatedItemType, 'flagged', {
        programId: params.programId,
        controlId: params.controlId,
        evidenceItemId: params.evidenceItemId,
      });
    }

    await fetchAll();
  };

  const updateFinding = async (findingId: string, updates: {
    status?: FindingStatus;
    notes?: string;
    assignedTo?: string | null;
    immediateAction?: string;
    correctiveAction?: string;
    auditCycleId?: string | null;
    programId?: string | null;
    controlId?: string | null;
    evidenceItemId?: string | null;
  }) => {
    if (!profile?.company_id) throw new Error('Not authenticated');

    const { error: updateError } = await supabase
      .from('audit_findings')
      .update({
        ...(updates.status !== undefined && { status: updates.status }),
        ...(updates.notes !== undefined && { notes: updates.notes || null }),
        ...(updates.assignedTo !== undefined && { assigned_to: updates.assignedTo }),
        ...(updates.immediateAction !== undefined && { immediate_action: updates.immediateAction || null }),
        ...(updates.correctiveAction !== undefined && { corrective_action: updates.correctiveAction || null }),
        ...(updates.auditCycleId !== undefined && { audit_cycle_id: updates.auditCycleId }),
        ...(updates.programId !== undefined && { program_id: updates.programId }),
        ...(updates.controlId !== undefined && { control_id: updates.controlId }),
        ...(updates.evidenceItemId !== undefined && { evidence_item_id: updates.evidenceItemId }),
        updated_at: new Date().toISOString(),
      })
      .eq('id', findingId)
      .eq('company_id', profile.company_id);

    if (updateError) throw updateError;
    await fetchAll();
  };

  const requestEvidence = async (params: {
    relatedItemId: string | null;
    relatedItemType: AuditItemType | null;
    programId?: string | null;
    controlId?: string | null;
    evidenceItemId?: string | null;
    evidenceType: string;
    description: string;
    deadline: string;
  }) => {
    if (!profile?.company_id || !profile?.id) throw new Error('Not authenticated');

    const { error: insertError } = await supabase.from('audit_evidence_requests').insert({
      company_id: profile.company_id,
      related_item_id: params.relatedItemId,
      related_item_type: params.relatedItemType,
      program_id: params.programId || null,
      control_id: params.controlId || null,
      evidence_item_id: params.evidenceItemId || null,
      evidence_type: params.evidenceType,
      description: params.description,
      deadline: params.deadline || null,
      requested_by: profile.id,
      status: 'pending',
    });

    if (insertError) throw insertError;

    if (params.relatedItemId && params.relatedItemType) {
      await upsertReviewStatus(params.relatedItemId, params.relatedItemType, 'pending_evidence', {
        programId: params.programId,
        controlId: params.controlId,
        evidenceItemId: params.evidenceItemId,
      });
    }
  };

  const upsertReviewStatus = async (
    itemId: string,
    itemType: AuditItemType,
    reviewStatus: ReviewStatus,
    context?: { programId?: string | null; controlId?: string | null; evidenceItemId?: string | null },
  ) => {
    if (!profile?.company_id || !profile?.id) return;

    await supabase.from('audit_review_items').upsert(
      {
        company_id: profile.company_id,
        item_id: itemId,
        item_type: itemType,
        program_id: context?.programId || null,
        control_id: context?.controlId || null,
        evidence_item_id: context?.evidenceItemId || null,
        review_status: reviewStatus,
        reviewed_by: reviewStatus === 'reviewed' ? profile.id : null,
        reviewed_at: reviewStatus === 'reviewed' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'company_id,item_id,item_type' },
    );

    await fetchAll();
  };

  const getReviewStatus = (itemId: string): ReviewStatus =>
    reviewItems.find((r) => r.item_id === itemId)?.review_status ?? 'not_reviewed';

  // Cycle CRUD
  const createCycle = async (params: { name: string; description?: string; scheduledDate?: string }) => {
    if (!profile?.company_id || !profile?.id) throw new Error('Not authenticated');

    const { error: insertError } = await supabase.from('audit_cycles').insert({
      company_id: profile.company_id,
      name: params.name,
      description: params.description || null,
      scheduled_date: params.scheduledDate || null,
      created_by: profile.id,
      status: 'planned',
    });

    if (insertError) throw insertError;
    await fetchAll();
  };

  const updateCycleStatus = async (cycleId: string, status: CycleStatus) => {
    if (!profile?.company_id) throw new Error('Not authenticated');

    const now = new Date().toISOString();
    const { error: updateError } = await supabase
      .from('audit_cycles')
      .update({
        status,
        ...(status === 'active' && { started_at: now }),
        ...(status === 'completed' && { completed_at: now }),
        updated_at: now,
      })
      .eq('id', cycleId)
      .eq('company_id', profile.company_id);

    if (updateError) throw updateError;
    await fetchAll();
  };

  return {
    findings,
    reviewItems,
    cycles,
    profiles,
    loading,
    error,
    refetch: fetchAll,
    raiseFinding,
    updateFinding,
    requestEvidence,
    upsertReviewStatus,
    getReviewStatus,
    createCycle,
    updateCycleStatus,
  };
}
